import CloseIcon from '@mui/icons-material/Close';
import { Grid, Input, Autocomplete as MuiAutocomplete } from '@mui/material';
import { InputSize, InputVariant, RoutalComputeInputTextStyles } from '@nx-smartmonkey/ui';
import lodash from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import Text from './Text';

type AutocompleteProps = {
  id?: string;
  fieldValue?: string;
  inputProps?: {
    tabIndex?: number;
    autoFocus?: boolean;
    disabled?: boolean;
    size?: InputSize;
    variant?: InputVariant;
    styles?: React.CSSProperties;
    placeholder?: string;
    placeholderIntlId?: string;
    placeholderIntlValues?: Record<string, string | number>;
  };
  containerProps?: {
    styles?: React.CSSProperties;
  };
  fullWidth?: boolean;
  emptyText?: string;
  emptyIntlId?: string;
  emptyIntlValues?: Record<string, string | number>;
  options: { value: string; label: string }[];
  removeIcon?: boolean;
  onBlur?: (...args: any[]) => any;
  onKeyDown?: (...args: any[]) => any;
  onUpdate: (...args: any[]) => any;
};

const Autocomplete = ({
  id,
  fieldValue,
  inputProps,
  containerProps,
  fullWidth,
  emptyText,
  emptyIntlId,
  emptyIntlValues,
  options,
  removeIcon,
  onBlur,
  onKeyDown,
  onUpdate,
}: AutocompleteProps) => {
  const [value, setValue] = useState<{ value: string; label: string } | null>(null);
  const [inputFocused, setInputFocused] = useState<boolean>(false);

  const intl = useIntl();

  useEffect(() => {
    if (fieldValue && !lodash.isEqual(fieldValue, value)) {
      const val = options.find((opt: { value: string; label: string }) => opt.value === fieldValue);
      setValue(val ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  const handleInputBlur = () => {
    setInputFocused(false);
  };
  const handleInputFocus = () => {
    setInputFocused(true);
  };

  // Select the content of the empty text
  const processedEmptyText: string | undefined = emptyIntlId
    ? intl.formatMessage({ id: emptyIntlId }, emptyIntlValues)
    : emptyText;

  return (
    <MuiAutocomplete
      id={`input-location${id ? `-${id}` : ``}`}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      getOptionLabel={(option: any) => (typeof option === `string` ? option : option.label ?? ``)}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      blurOnSelect
      value={value}
      fullWidth={fullWidth}
      noOptionsText={processedEmptyText}
      onChange={async (event: any, selectedOption: { value: string; label: string } | null) => {
        setValue(selectedOption);
        onUpdate(selectedOption ? selectedOption.value : null);
      }}
      renderInput={(params: any) => {
        const computedStyles = RoutalComputeInputTextStyles({
          size: inputProps?.size,
          variant: inputProps?.variant,
          inputFocused,
          inputStyles: inputProps?.styles,
        });

        let processedPlaceholder: string;

        // Select the content of the placeholder
        if (inputProps?.placeholderIntlId) {
          processedPlaceholder = intl.formatMessage(
            { id: inputProps.placeholderIntlId },
            inputProps.placeholderIntlValues
          );
        } else {
          processedPlaceholder = inputProps?.placeholder ?? intl.formatMessage({ id: `none` });
        }

        return (
          <Input
            ref={params.InputProps.ref}
            tabIndex={inputProps?.tabIndex}
            autoFocus={inputProps?.autoFocus}
            type="text"
            disableUnderline
            disabled={inputProps?.disabled}
            fullWidth={fullWidth}
            placeholder={processedPlaceholder}
            style={{
              ...computedStyles.inputContainer,
              ...(!fullWidth ? { width: `250px` } : {}),
              ...(containerProps?.styles ?? {}),
            }}
            inputProps={{
              ...params.inputProps,
              ...{ style: computedStyles.input },
              id: `location-input${id ? `-${id}` : ``}`,
            }}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            endAdornment={
              removeIcon && value ? (
                <CloseIcon
                  style={{
                    width: `20px`,
                    height: `20px`,
                    margin: `0 4px`,
                    color: `#777777`,
                  }}
                  onClick={() => {
                    setValue(null);
                    onUpdate(null);
                  }}
                />
              ) : null
            }
          />
        );
      }}
      renderOption={(props, opt: { value: string; label: string }) => (
        <li {...props}>
          <Grid container alignItems="center" wrap="nowrap">
            {opt ? (
              <Grid item>
                <Text variant="body">{opt.label ?? ``}</Text>
              </Grid>
            ) : (
              <Text variant="body" intlId="no_results" />
            )}
          </Grid>
        </li>
      )}
    />
  );
};

export default Autocomplete;
